import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Plus, Trash2, Calculator, FileText } from 'lucide-react';

export default function CreateBill() {
  const navigate = useNavigate();
  const [props, setProps] = useState([]);
  const [packages, setPackages] = useState([]);
  const [propsCategories, setPropsCategories] = useState([]);
  const [photographyCategories, setPhotographyCategories] = useState([]);

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    paymentStatus: 'pending',
    paymentMethod: ''
  });
  const [billItems, setBillItems] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState('both'); // 'props', 'photography', 'both'
  const [selectedPropsCategory, setSelectedPropsCategory] = useState('all');
  const [selectedPhotographyCategory, setSelectedPhotographyCategory] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [propsSnapshot, packagesSnapshot, propsCategoriesSnapshot, photographyCategoriesSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'props'), orderBy('name'))),
        getDocs(query(collection(db, 'photographyPackages'), orderBy('name'))),
        getDocs(query(collection(db, 'propsCategories'), orderBy('name'))),
        getDocs(query(collection(db, 'photographyCategories'), orderBy('name')))
      ]);

      const propsData = propsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'props'
      }));

      const packagesData = packagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'photography'
      }));

      const propsCategoriesData = propsCategoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const photographyCategoriesData = photographyCategoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setProps(propsData);
      setPackages(packagesData);
      setPropsCategories(propsCategoriesData);
      setPhotographyCategories(photographyCategoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const addBillItem = (item, quantity = 1, customRate = null) => {
    const rate = customRate || (item.type === 'props' ? item.rentalRate : item.price);
    const newItem = {
      id: Date.now(),
      itemId: item.id,
      type: item.type,
      name: item.name,
      quantity: quantity,
      rate: rate,
      total: quantity * rate
    };
    setBillItems([...billItems, newItem]);
  };

  const updateBillItem = (itemId, field, value) => {
    setBillItems(items => 
      items.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'rate') {
            updated.total = updated.quantity * updated.rate;
          }
          return updated;
        }
        return item;
      })
    );
  };

  const removeBillItem = (itemId) => {
    setBillItems(items => items.filter(item => item.id !== itemId));
  };

  const calculateTotal = () => {
    return billItems.reduce((sum, item) => sum + item.total, 0);
  };

  // Filter functions for categories
  const getFilteredProps = () => {
    if (selectedPropsCategory === 'all') {
      return props.filter(prop => prop.status === 'active');
    }
    return props.filter(prop => prop.status === 'active' && prop.categoryId === selectedPropsCategory);
  };

  const getFilteredPackages = () => {
    if (selectedPhotographyCategory === 'all') {
      return packages;
    }
    return packages.filter(pkg => pkg.categoryId === selectedPhotographyCategory);
  };

  const handleSaveBill = async () => {
    try {
      setSaving(true);
      
      if (!formData.customerName || !formData.customerPhone || billItems.length === 0) {
        alert('Please fill in customer details and add at least one item');
        return;
      }

      const billData = {
        customerName: formData.customerName,
        customerPhone: formData.customerPhone,
        items: billItems,
        total: calculateTotal(),
        status: formData.paymentStatus,
        paymentMethod: formData.paymentMethod,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'bills'), billData);
      
      // Show success message
      alert('✅ Bill created successfully! Redirecting to Bills Management...');
      
      // Small delay to let user see the success message, then navigate
      setTimeout(() => {
        navigate('/billing/manage');
      }, 1000);
    } catch (error) {
      console.error('Error saving bill:', error);
      alert('Failed to create bill. Please try again.');
    } finally {
      setSaving(false);
    }
  };



  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Create New Bill</h1>
        <p className="mt-2 text-gray-600">
          {selectedServiceType === 'photography' && 'Generate bills for photography services'}
          {selectedServiceType === 'props' && 'Generate bills for props rental services'}
          {selectedServiceType === 'both' && 'Generate bills for photography and props rental services'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Customer Information */}
        <div className="lg:col-span-2">
          <div className="card mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Customer Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Name *
                </label>
                <input
                  type="text"
                  required
                  className="input-field"
                  value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                  placeholder="Enter customer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  className="input-field"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Type *
                </label>
                <select
                  className="input-field"
                  value={selectedServiceType}
                  onChange={(e) => setSelectedServiceType(e.target.value)}
                >
                  <option value="both">📸📦 Photography + Props</option>
                  <option value="photography">📸 Photography Only</option>
                  <option value="props">📦 Props Rental Only</option>
                </select>
              </div>
            </div>

            {/* Payment Information Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Status
                  </label>
                  <select
                    className="input-field"
                    value={formData.paymentStatus}
                    onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value })}
                  >
                    <option value="pending">🟡 Pending</option>
                    <option value="paid">🟢 Paid</option>
                    <option value="overdue">🔴 Overdue</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    className="input-field"
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  >
                    <option value="">Select payment method</option>
                    <option value="cash">💵 Cash</option>
                    <option value="upi">📱 UPI</option>
                    <option value="card">💳 Card</option>
                    <option value="bank_transfer">🏦 Bank Transfer</option>
                    <option value="cheque">📝 Cheque</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Bill Items */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Bill Items</h2>
            
            {/* Add Items Section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Services & Items</h3>
              
              {/* Photography Packages - Show only if photography is selected */}
              {(selectedServiceType === 'photography' || selectedServiceType === 'both') && (
                <div className="mb-6">
                  <h4 className="text-md font-medium text-primary-700 mb-3 flex items-center">
                    <span className="bg-primary-100 p-1 rounded mr-2">📸</span>
                    Photography Packages
                  </h4>
                  
                  {/* Photography Category Filter */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Photography Category
                    </label>
                    <select
                      className="input-field"
                      value={selectedPhotographyCategory}
                      onChange={(e) => setSelectedPhotographyCategory(e.target.value)}
                    >
                      <option value="all">📸 All Photography Categories</option>
                      {photographyCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {getFilteredPackages().length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {getFilteredPackages().map((pkg) => (
                        <button
                          key={pkg.id}
                          onClick={() => addBillItem(pkg)}
                          className="text-left p-4 border-2 border-primary-200 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 group"
                        >
                          <div className="font-semibold text-gray-900 group-hover:text-primary-700">{pkg.name}</div>
                          <div className="text-primary-600 font-medium text-lg">₹{pkg.price?.toLocaleString()}</div>
                          {pkg.description && (
                            <div className="text-gray-500 text-sm mt-1 line-clamp-2">{pkg.description}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      <div className="text-4xl mb-2">📸</div>
                      <p>
                        {selectedPhotographyCategory === 'all' 
                          ? 'No photography packages available' 
                          : 'No packages in selected category'
                        }
                      </p>
                      <p className="text-sm">
                        {selectedPhotographyCategory === 'all'
                          ? 'Add packages in Photography management'
                          : 'Try selecting "All Categories" or add packages to this category'
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Props Rental - Show only if props is selected */}
              {(selectedServiceType === 'props' || selectedServiceType === 'both') && (
                <div>
                  <h4 className="text-md font-medium text-green-700 mb-3 flex items-center">
                    <span className="bg-green-100 p-1 rounded mr-2">📦</span>
                    Props Rental
                  </h4>
                  
                  {/* Props Category Filter */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Props Category
                    </label>
                    <select
                      className="input-field"
                      value={selectedPropsCategory}
                      onChange={(e) => setSelectedPropsCategory(e.target.value)}
                    >
                      <option value="all">📦 All Props Categories</option>
                      {propsCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {getFilteredProps().length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {getFilteredProps().map((prop) => (
                        <button
                          key={prop.id}
                          onClick={() => addBillItem(prop)}
                          className="text-left p-3 border-2 border-green-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200 group flex items-center space-x-3"
                        >
                          {prop.imageUrl ? (
                            <img 
                              src={prop.imageUrl} 
                              alt={prop.name} 
                              className="w-12 h-12 object-cover rounded-md"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-green-100 rounded-md flex items-center justify-center">
                              <span className="text-green-600">📦</span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 group-hover:text-green-700">{prop.name}</div>
                            <div className="text-green-600 font-medium">₹{prop.rentalRate}/day</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      <div className="text-4xl mb-2">📦</div>
                      <p>
                        {selectedPropsCategory === 'all' 
                          ? 'No props available' 
                          : 'No props in selected category'
                        }
                      </p>
                      <p className="text-sm">
                        {selectedPropsCategory === 'all'
                          ? 'Add props in Props management'
                          : 'Try selecting "All Categories" or add props to this category'
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Show message when no service type is effectively selected */}
              {selectedServiceType !== 'photography' && selectedServiceType !== 'props' && selectedServiceType !== 'both' && (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-4xl mb-4">🔍</div>
                  <p className="text-lg font-medium">Select a service type above</p>
                  <p>Choose Photography, Props Rental, or Both to see available items</p>
                </div>
              )}
            </div>

            {/* Bill Items Table */}
            {billItems.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {billItems.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 capitalize">{item.type}</td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                            value={item.quantity}
                            onChange={(e) => updateBillItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            value={item.rate}
                            onChange={(e) => updateBillItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">₹{item.total.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => removeBillItem(item.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {billItems.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📋</div>
                <p>Select photography packages or props above to add them to the bill</p>
              </div>
            )}
          </div>
        </div>

        {/* Bill Summary */}
        <div className="lg:col-span-1">
          <div className="card sticky top-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Calculator className="h-5 w-5 mr-2" />
              Bill Summary
            </h2>
            
            {/* Service Type Indicator */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-1">Service Type:</p>
              <p className="text-sm text-gray-600">
                {selectedServiceType === 'both' && '📸📦 Photography + Props'}
                {selectedServiceType === 'photography' && '📸 Photography Only'}
                {selectedServiceType === 'props' && '📦 Props Rental Only'}
              </p>
              
              {/* Category Filters Summary */}
              {(selectedServiceType === 'photography' || selectedServiceType === 'both') && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Photography: {selectedPhotographyCategory === 'all' ? 'All Categories' : 
                      photographyCategories.find(cat => cat.id === selectedPhotographyCategory)?.name || 'Unknown Category'}
                  </p>
                </div>
              )}
              
              {(selectedServiceType === 'props' || selectedServiceType === 'both') && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Props: {selectedPropsCategory === 'all' ? 'All Categories' : 
                      propsCategories.find(cat => cat.id === selectedPropsCategory)?.name || 'Unknown Category'}
                  </p>
                </div>
              )}
            </div>

            {/* Payment Information Summary */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-1">Payment Information:</p>
              <div className="text-sm text-gray-600">
                <p>
                  Status: {formData.paymentStatus === 'pending' && '🟡 Pending'}
                  {formData.paymentStatus === 'paid' && '🟢 Paid'}
                  {formData.paymentStatus === 'overdue' && '🔴 Overdue'}
                </p>
                {formData.paymentMethod && (
                  <p>
                    Method: {formData.paymentMethod === 'cash' && '💵 Cash'}
                    {formData.paymentMethod === 'upi' && '📱 UPI'}
                    {formData.paymentMethod === 'card' && '💳 Card'}
                    {formData.paymentMethod === 'bank_transfer' && '🏦 Bank Transfer'}
                    {formData.paymentMethod === 'cheque' && '📝 Cheque'}
                  </p>
                )}
                {!formData.paymentMethod && (
                  <p className="text-gray-400">No payment method selected</p>
                )}
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between text-xl font-bold">
                <span>Total Amount</span>
                <span className="text-primary">₹{calculateTotal().toLocaleString()}</span>
              </div>
              
              {billItems.length > 0 && (
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  <p className="font-medium mb-1">Bill includes:</p>
                  <ul className="space-y-1">
                    {billItems.map((item, index) => (
                      <li key={index} className="flex justify-between">
                        <span>{item.name} (x{item.quantity})</span>
                        <span>₹{item.total.toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <button
                onClick={handleSaveBill}
                disabled={saving || billItems.length === 0 || !formData.customerName || !formData.customerPhone}
                className="w-full btn-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="h-4 w-4 mr-2" />
                {saving ? 'Creating Bill...' : 'Create Bill & Manage Bills'}
              </button>
              
              {(billItems.length === 0 || !formData.customerName || !formData.customerPhone) && (
                <p className="text-sm text-gray-500 text-center mt-2">
                  {!formData.customerName || !formData.customerPhone 
                    ? 'Fill customer details to create bill' 
                    : 'Add items to create bill'
                  }
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
